import React, { FC, useEffect } from 'react';

import { Icon } from '@iconify/react';
import clsx from 'clsx';

import Api from '~/Api';
import Button from '~/components/atoms/buttons/Button';
import { useModalContext } from '~/components/layoutComponents/modal/ModalContext';
import { useSnackBarContext } from '~/components/layoutComponents/snackbar/SnackbarContext';
import Pagination from '~/components/molecules/Pagination';
import TopHeadlineRow from '~/components/molecules/TopHeadlineRow';
import TotalCount from '~/components/molecules/TotalCount';
import useFilters from '~/hooks/useFilters';
import useQueryParams from '~/hooks/useQueryParams';
import CreateForm from '~/modules/plans/components/PlansList/CreateForm';
import Table from '~/modules/plans/components/PlansList/Table';
import useData, {
  FiltersType,
} from '~/modules/plans/components/PlansList/useData';
import { PlanType } from '~/modules/plans/types';
import { SORT_ORDER } from '~/types/common';

import styles from './styles.modules.scss';

const DEFAULT_FILTER_STATE: FiltersType = {
  sortOrder: SORT_ORDER.DESC,
  sortBy: 'created_at',
  page: 1,
  'per-page': 10,
};

const PlansList: FC = () => {
  const { handleOpenModal } = useModalContext();
  const { showSnackbar } = useSnackBarContext();
  const { getQueryParams, setQueryParams } = useQueryParams();

  const defaultFilterState = {
    ...DEFAULT_FILTER_STATE,
    ...getQueryParams(),
  };

  const { filters, updateFilter } = useFilters<FiltersType>(defaultFilterState);

  const { plans, isLoading, meta, updateItems } = useData(filters);

  useEffect(() => {
    setQueryParams(filters);
  }, [filters]);

  const handleAdd = async (data: Partial<PlanType>): Promise<void> => {
    try {
      await Api.createPlan(data);
      await updateItems();
      showSnackbar('Plan is created', 'success');
    } catch (e: any) {
      showSnackbar(e.message);
      console.error(e);
    }
  };

  return (
    <div
      className={clsx(
        { [styles.loading]: isLoading },
        styles.vertical_flex_container,
        styles['vertical_flex_container--full_height'],
      )}
    >
      <TopHeadlineRow className={styles.top_bar} text="Plans">
        <Button
          variant="outlined"
          onClick={(): void => {
            handleOpenModal({
              component: ({ onClose }) => (
                <CreateForm addItem={handleAdd} onClose={onClose} />
              ),
            });
          }}
        >
          <Icon
            style={{ marginRight: 5 }}
            fontSize={20}
            icon="material-symbols:person-add-rounded"
          />
          New Plan
        </Button>
      </TopHeadlineRow>
      <div
        className={clsx(
          styles.container,
          styles.vertical_flex_container__full_parent_height,
        )}
      >
        <div className={styles.container__left}>
          <TotalCount count={meta.totalCount} />
          <div
            className={clsx(
              styles.vertical_flex_container,
              styles['vertical_flex_container--full_height'],
            )}
          >
            <div className={styles.vertical_flex_container__full_parent_height}>
              <Table
                data={plans}
                filter={{
                  sortBy: filters.sortBy,
                  sortOrder: filters.sortOrder,
                }}
                applyFilters={(filters): void => updateFilter(filters)}
              />
            </div>
            {meta.totalPages > 1 && (
              <Pagination
                currentPage={Number(meta.currentPage)}
                totalPages={Number(meta.totalPages)}
                setPage={(page: number): void => {
                  updateFilter({ page });
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlansList;
